import { useState } from "react";
import { useCreateCustomPrintout, useDeleteCustomPrintout, useRenameCustomPrintout, useSaveCustomPrintoutVersion } from "../../../hooks/useCustomPrintouts";
import type { CustomPrintoutSummary } from "./customPrintoutTypes";
import type { CustomPrintoutDraftHandle } from "./useCustomPrintoutDraft";
import type { useCustomPrintoutSelection } from "./useCustomPrintoutSelection";

/** The name dialog, per opening (`key` remounts the field with `initialName`). */
export type NameDialogState = { mode: "create" | "rename"; key: number; initialName: string };

type Params = {
	selection: ReturnType<typeof useCustomPrintoutSelection>;
	draft: CustomPrintoutDraftHandle;
	/** The shown printout's list row, when it is a saved one. */
	selected: CustomPrintoutSummary | undefined;
};

/**
 * Save (a new printout asks for its name, a saved one confirms the new version), Discard (confirmed), Rename
 * and Delete, with the dialogs they open.
 */
export const useCustomPrintoutActions = ({ selection, draft, selected }: Params) => {
	const create = useCreateCustomPrintout();
	const saveVersion = useSaveCustomPrintoutVersion();
	const rename = useRenameCustomPrintout();
	const remove = useDeleteCustomPrintout();
	const [nameDialog, setNameDialog] = useState<NameDialogState | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<CustomPrintoutSummary | null>(null);
	const [isConfirmingSave, setIsConfirmingSave] = useState<boolean>(false);
	const [isConfirmingDiscard, setIsConfirmingDiscard] = useState<boolean>(false);
	const { printoutId, select, nextAfterDelete } = selection;

	const openNameDialog = (mode: NameDialogState["mode"], initialName: string) =>
		setNameDialog((current) => ({ mode, initialName, key: current === null ? 0 : current.key + 1 }));

	// A saved printout's Save changes what the whole department prints, so it is confirmed first; a new printout's
	// name dialog is its confirmation.
	const save = (): void => {
		if (draft.draft === null) return;
		if (printoutId === null) openNameDialog("create", "");
		else setIsConfirmingSave(true);
	};

	const confirmSave = (): void => {
		if (printoutId === null || draft.draft === null) throw new Error("a new version was confirmed with no saved printout or no design");
		saveVersion.mutate({ printoutId, generationId: draft.draft.generationId }, { onSuccess: draft.discard, onSettled: () => setIsConfirmingSave(false) });
	};

	const confirmDiscard = (): void => {
		setIsConfirmingDiscard(false);
		draft.discard();
	};

	const confirmName = (name: string): void => {
		if (nameDialog === null) return;
		if (nameDialog.mode === "create" && draft.draft !== null) {
			const onCreated = (created: { id: string }): void => {
				setNameDialog(null);
				// The saved printout now holds what the draft was, so the draft is done with.
				draft.discard();
				select(created.id);
			};
			create.mutate({ name, generationId: draft.draft.generationId }, { onSuccess: onCreated });
		} else if (nameDialog.mode === "rename" && printoutId !== null) {
			rename.mutate({ printoutId, name }, { onSuccess: () => setNameDialog(null) });
		}
	};

	const confirmDelete = (): void => {
		if (deleteTarget === null) return;
		const onDeleted = (): void => {
			draft.discard();
			select(nextAfterDelete(deleteTarget.id));
		};
		remove.mutate({ printoutId: deleteTarget.id }, { onSuccess: onDeleted, onSettled: () => setDeleteTarget(null) });
	};

	return {
		save,
		isSaving: saveVersion.isPending || create.isPending,
		isConfirmingSave,
		confirmSave,
		cancelSave: () => setIsConfirmingSave(false),
		isConfirmingDiscard,
		askDiscard: () => setIsConfirmingDiscard(true),
		confirmDiscard,
		cancelDiscard: () => setIsConfirmingDiscard(false),
		nameDialog,
		isNaming: create.isPending || rename.isPending,
		openRename: () => {
			if (selected !== undefined) openNameDialog("rename", selected.name);
		},
		closeName: () => setNameDialog(null),
		confirmName,
		deleteTarget,
		isDeleting: remove.isPending,
		openDelete: () => setDeleteTarget(selected === undefined ? null : selected),
		closeDelete: () => setDeleteTarget(null),
		confirmDelete,
	};
};

export type CustomPrintoutActions = ReturnType<typeof useCustomPrintoutActions>;
